<template>
  <div>
    <!-- Opened on top of the Modifier Options modal (UpdateModifierOptionGroupForItem) by show():
         new Modal().show() + the shared #custom-overlay, like ModalChoicePriceSchedules over
         AddItem. z-index above that modal (1055) and the app sidenav (9999). -->
    <div
      class="modal fade"
      id="modalModifierPriceSchedules"
      tabindex="-1"
      aria-hidden="true"
      style="z-index: 10056"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Scheduled Price<span v-if="sectionName"> · {{ sectionName }}</span>
            </h5>
            <i
              class="material-icons btn-close-modal btn-close-modal-modifier-price-schedules justify-content-end cursor-pointer"
              data-bs-dismiss="modal"
              aria-label="Close"
              >clear</i
            >
          </div>
          <div class="modal-body">
            <div class="ps-hint">
              <i class="material-icons ps-hint__icon">info</i>
              <div>
                Inside a switched-on schedule (its dates, days and hours, Singapore time GMT+8),
                the options of this section under this item sell at the price list picked here
                instead of {{ usualLabel }}, on every channel (POS, MOO, QR and KIOSK) of every
                branch using this menu set. If schedules overlap, the one higher in the list
                applies. An option with no price on that list keeps its usual price.
              </div>
            </div>

            <div v-if="!rows.length" class="ps-empty">No schedules yet.</div>

            <div
              v-for="(schedule, index) in rows"
              :key="index"
              class="ps-card"
              :class="{ 'ps-card--off': !schedule.status, 'ps-card--invalid': errors[index] }"
            >
              <div class="ps-card__head">
                <span class="ps-card__title">Schedule {{ index + 1 }}</span>
                <span class="ps-card__move">
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === 0 }"
                    title="Move up (higher wins)"
                    @click="move(index, -1)"
                    >arrow_upward</i
                  >
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === rows.length - 1 }"
                    title="Move down"
                    @click="move(index, 1)"
                    >arrow_downward</i
                  >
                </span>
                <div class="form-check form-switch mb-0 ms-auto">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'modifier_price_schedule_status_' + index"
                    :checked="!!schedule.status"
                    @change="schedule.status = $event.target.checked ? 1 : 0"
                  />
                  <label class="form-check-label mb-0" :for="'modifier_price_schedule_status_' + index">
                    {{ schedule.status ? "On" : "Off" }}
                  </label>
                </div>
                <i
                  class="material-icons ps-card__remove"
                  title="Remove schedule"
                  @click="rows.splice(index, 1)"
                  >delete_outline</i
                >
              </div>

              <div class="ps-card__body">
                <ScheduleWhenFields
                  :schedule="schedule"
                  :id-prefix="'modifier_price_schedule_' + index"
                  @update="Object.assign(schedule, $event)"
                />

                <div class="ps-field ps-key">
                  <div class="ps-field__label">Price List</div>
                  <Multiselect
                    v-model="schedule.price_key"
                    :options="priceList"
                    placeholder="Select"
                    :canClear="false"
                    :canDeselect="false"
                  />
                </div>

                <div v-if="schedule.price_key && options.length" class="ps-preview">
                  <div v-for="(option, optionIndex) in options" :key="optionIndex" class="ps-preview__row">
                    <span class="ps-preview__name">{{ option.name }}</span>
                    <span>
                      <template v-if="scheduledPrice(option, schedule.price_key)">
                        <span class="ps-preview__usual">{{ option.usual }}</span>
                        → <strong>{{ scheduledPrice(option, schedule.price_key) }}</strong>
                      </template>
                      <template v-else>{{ option.usual }} <span class="ps-preview__keep">(no price, kept)</span></template>
                    </span>
                  </div>
                </div>

                <div class="text-danger ps-error" v-if="errors[index]">{{ errors[index] }}</div>
              </div>
            </div>

            <button
              type="button"
              class="ps-add"
              :disabled="rows.length >= max"
              @click="addRow"
            >
              <i class="material-icons">add</i> Add schedule
            </button>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" data-bs-dismiss="modal">
              Cancel
            </button>
            <material-button class="btn btn-sm btn-background mb-0" @click="apply">
              Apply
            </material-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import Modal from "bootstrap/js/dist/modal";
import Multiselect from "@vueform/multiselect";
import MaterialButton from "@/components/MaterialButton.vue";
import { isUnrestrictedWhen, newScheduleWhen, scheduleWhenError, scheduleWhenFrom, scheduleWhenPayload } from "@/config/priceSchedule";
import ScheduleWhenFields from "@/components/ScheduleWhenFields.vue";

const amountOf = (formatted) => parseFloat(String(formatted || "").replace(/[^0-9.-]/g, "")) || 0;

// Scheduled price lists of one modifier section under one main item of the menu set, built on
// ModalChoicePriceSchedules. Works on a copy: Apply checks it (same rules as the API) and hands it
// back to the Modifier Options modal, which sends it on Save; Cancel drops it.
export default {
  name: "ModalModifierPriceSchedules",
  components: { MaterialButton, Multiselect, ScheduleWhenFields },
  emits: ["apply"],
  data() {
    return {
      rows: [],
      sectionName: "",
      priceList: [],
      usualKey: "",
      // The section's options with their price on every price list (item_price_formats).
      options: [],
      // Incomplete rows are flagged only after Apply; clashes show right away.
      checked: false,
      // Same limit as the API (MintyModifierSectionPriceSchedule::MAX_PER_SECTION).
      max: 10,
    };
  },
  computed: {
    usualLabel() {
      const usual = this.priceList.find((option) => option.value === this.usualKey);
      return usual ? `its usual ${usual.label}` : "its usual price list";
    },
    // One message (or "") per row; a missing price list only after Apply.
    errors() {
      return this.rows.map((schedule) => {
        const when = scheduleWhenError(schedule);
        if (when) {
          return when;
        }
        if (this.checked && !schedule.price_key) {
          return "Pick a price list.";
        }
        if (this.checked && isUnrestrictedWhen(schedule)) {
          return "Set dates, days or hours, or this price would apply all the time.";
        }
        return "";
      });
    },
  },
  methods: {
    // Fill the editor with the section as the Modifier Options modal holds it, then show it.
    open(section) {
      this.rows = (section.price_schedules || []).map((schedule) => ({ ...schedule, ...scheduleWhenFrom(schedule) }));
      this.sectionName = `(${section.id}) ${section.name || ""}`.trim();
      this.priceList = section.price_list || [];
      this.usualKey = section.other_price_list_id || section.price_list_id || "";
      this.options = (section.items || []).map((item) => ({
        name: item.item_name_format || item.item_name,
        usual: (item.item_price_formats && item.item_price_formats[this.usualKey]) || item.item_price_format,
        formats: item.item_price_formats || {},
      }));
      this.checked = false;
      this.show();
    },
    // Same pattern as AddItem.showStackedModal(): the Modifier Options modal was opened through
    // the Bootstrap bundle, this one through the `Modal` import, a separate copy of Bootstrap, so
    // that modal's focus trap (a focusin listener on document) would keep pulling focus out of
    // these inputs. focusin is stopped as it bubbles out of this modal, not in the capture phase:
    // the price list Multiselect opens on its own focusin, which a capture-phase stop never lets
    // reach it.
    show() {
      const overlay = document.getElementById("custom-overlay");
      overlay?.classList.add("show");
      const modalElement = document.getElementById("modalModifierPriceSchedules");
      if (!modalElement) {
        return;
      }
      const guardFocus = (event) => event.stopPropagation();
      modalElement.addEventListener("focusin", guardFocus);
      Modal.getOrCreateInstance(modalElement).show();
      modalElement.addEventListener(
        "hidden.bs.modal",
        () => {
          overlay?.classList.remove("show");
          modalElement.removeEventListener("focusin", guardFocus);
          // Hiding this modal clears body.modal-open, which the Modifier Options modal underneath
          // still needs (no page scroll behind it).
          if (document.querySelector("#modalUpdateModifierOptionGroupForItem.show")) {
            document.body.classList.add("modal-open");
          }
        },
        { once: true }
      );
    },
    // The option's price on the list, or "" when it has none there (it keeps its usual price).
    scheduledPrice(option, priceKey) {
      const formatted = option.formats[priceKey];
      return amountOf(formatted) > 0 ? formatted : "";
    },
    addRow() {
      this.rows.push({ ...newScheduleWhen(), price_key: "", status: 1 });
    },
    // Overlapping schedules: the one higher in the list wins, so the order is the priority.
    move(index, step) {
      const target = index + step;
      if (target < 0 || target >= this.rows.length) {
        return;
      }
      const [row] = this.rows.splice(index, 1);
      this.rows.splice(target, 0, row);
    },
    apply() {
      this.checked = true;
      if (this.errors.some(Boolean)) {
        return;
      }
      this.$emit(
        "apply",
        this.rows.map((schedule) => ({
          ...scheduleWhenPayload(schedule),
          price_key: schedule.price_key,
          status: schedule.status ? 1 : 0,
        }))
      );
      $(".btn-close-modal-modifier-price-schedules").click();
    },
  },
};
</script>

<style scoped>
.ps-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f4f6f9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #67748e;
}
.ps-hint__icon {
  font-size: 18px;
  color: #8a94a6;
}
.ps-empty {
  font-size: 14px;
  color: #7b809a;
  text-align: center;
  padding: 18px 0 8px;
}
.ps-error {
  font-size: 13px;
}
.ps-card {
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: visible;
}
.ps-card--off {
  background: #f8f9fa;
}
.ps-card--off .ps-card__body {
  opacity: 0.55;
}
.ps-card--invalid {
  border-color: #f44335;
}
.ps-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f3;
}
.ps-card__title {
  font-weight: 600;
  color: #344767;
}
.ps-card__move {
  display: inline-flex;
  gap: 2px;
  margin-left: 6px;
}
.ps-card__move .material-icons {
  font-size: 18px;
  color: #7b809a;
  cursor: pointer;
}
.ps-card__move .ps-card__move--off {
  opacity: 0.3;
  pointer-events: none;
}
.ps-card__remove {
  cursor: pointer;
  color: #9aa0ac;
  font-size: 20px;
}
.ps-card__remove:hover {
  color: #f44335;
}
.ps-card__body {
  padding: 12px 14px 14px;
  display: grid;
  gap: 14px;
}
.ps-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.ps-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ps-key {
  max-width: 220px;
}
.ps-preview {
  border: 1px solid #eef0f3;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #344767;
  max-height: 180px;
  overflow-y: auto;
}
.ps-preview__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0;
}
.ps-preview__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ps-preview__usual {
  color: #9aa0ac;
  text-decoration: line-through;
}
.ps-preview__keep {
  color: #9aa0ac;
}
.ps-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #c4cad3;
  border-radius: 10px;
  background: transparent;
  color: #344767;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  justify-content: center;
  padding: 10px;
}
.ps-add:hover:not(:disabled) {
  background: #f4f6f9;
}
.ps-add:disabled {
  opacity: 0.5;
}
.ps-add .material-icons {
  font-size: 18px;
}
</style>
